import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaPercentage, FaChartLine, FaInfoCircle, FaCheckCircle, FaQuestionCircle } from 'react-icons/fa';
import Chart from 'react-apexcharts';

const APYCalculator = () => {
  const [formData, setFormData] = useState({
    initialDeposit: '',
    interestRate: '',
    compoundingFrequency: 'annual',
    years: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    // Initialize chart data
    const chartOptions = {
      series: [0, 0],
      options: {
        chart: {
          type: 'donut',
          height: 350,
          toolbar: {
            show: false
          }
        },
        colors: ['#4361ee', '#8338ec'],
        labels: ['Initial Deposit', 'Interest Earned'],
        legend: {
          position: 'bottom',
          fontSize: '14px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          labels: {
            colors: '#666',
            useSeriesColors: true
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      }
    };
    setChartData(chartOptions);
  }, []);

  const calculateAPY = () => {
    const { initialDeposit, interestRate, compoundingFrequency, years } = formData;

    if (!initialDeposit || !interestRate || !years) {
      setError('Please fill in all required fields');
      return;
    }

    const deposit = parseFloat(initialDeposit);
    const rate = parseFloat(interestRate) / 100;
    const numYears = parseFloat(years);

    if (isNaN(deposit) || isNaN(rate) || isNaN(numYears) || deposit <= 0 || rate <= 0 || numYears <= 0) {
      setError('Please enter valid numbers for all fields');
      return;
    }

    // Calculate number of compounding periods per year
    let periodsPerYear = 1;
    switch (compoundingFrequency) {
      case 'daily':
        periodsPerYear = 365;
        break;
      case 'weekly':
        periodsPerYear = 52;
        break;
      case 'monthly':
        periodsPerYear = 12;
        break;
      case 'quarterly':
        periodsPerYear = 4;
        break;
      case 'semiannual':
        periodsPerYear = 2;
        break;
      case 'annual':
        periodsPerYear = 1;
        break;
      default:
        periodsPerYear = 1;
    }

    // Calculate APY
    const apy = Math.pow(1 + (rate / periodsPerYear), periodsPerYear) - 1;
    const apyPercentage = apy * 100;

    // Calculate final amount
    const totalPeriods = periodsPerYear * numYears;
    const finalAmount = deposit * Math.pow(1 + (rate / periodsPerYear), totalPeriods);
    const interestEarned = finalAmount - deposit;

    setResults({
      apy: `${apyPercentage.toFixed(2)}%`,
      finalAmount: `${finalAmount.toFixed(2)}`,
      interestEarned: `${interestEarned.toFixed(2)}`,
      totalYears: numYears,
      periodsPerYear: periodsPerYear
    });

    // Update chart data
    if (chartData) {
      const updatedData = {
        ...chartData,
        series: [deposit, interestEarned]
      };
      setChartData(updatedData);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>APY Calculator | Financial Tool</title>
        <meta name="description" content="Calculate your Annual Percentage Yield (APY) and see how much interest you can earn on your savings or investments." />
        <meta name="keywords" content="apy calculator, apy, annual percentage yield, interest calculator, savings calculator" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="calculator-header mb-12">
          <div className="flex items-center gap-4">
            <div className="header-icon text-4xl">
              <FaPercentage />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">APY Calculator</h1>
              <p className="text-lg text-blue-600 font-semibold">Calculate Your Annual Percentage Yield</p>
            </div>
          </div>
        </div>

        <div className="calculator-description space-y-4 mb-12">
          <p className="text-gray-700">
            The APY Calculator helps you understand the effective annual return on your savings or investments,
            taking into account the compounding frequency.
          </p>
        </div>

        <div className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="initialDeposit">
                Initial Deposit
              </label>
              <input
                type="number"
                id="initialDeposit"
                name="initialDeposit"
                value={formData.initialDeposit}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="₹0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="interestRate">
                Annual Interest Rate (%)
              </label>
              <input
                type="number"
                id="interestRate"
                name="interestRate"
                value={formData.interestRate}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="compoundingFrequency">
                Compounding Frequency
              </label>
              <select
                id="compoundingFrequency"
                name="compoundingFrequency"
                value={formData.compoundingFrequency}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="semiannual">Semi-Annual</option>
                <option value="annual">Annual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="years">
                Number of Years
              </label>
              <input
                type="number"
                id="years"
                name="years"
                value={formData.years}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="0"
              />
            </div>

            <button
              onClick={calculateAPY}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-md text-sm font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Calculate APY
            </button>

            {error && (
              <div className="text-red-500 text-center mt-4">
                {error}
              </div>
            )}
          </div>

          {results && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">APY (Annual Percentage Yield)</h3>
                  <p className="text-xl font-medium">{results.apy}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Final Amount</h3>
                  <p className="text-xl font-medium">₹{results.finalAmount}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Interest Earned</h3>
                  <p className="text-xl font-medium">₹{results.interestEarned}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Compounding Periods per Year</h3>
                  <p className="text-xl font-medium">{results.periodsPerYear}</p>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Distribution Visualization</h3>
                <div className="h-96">
                  {chartData && (
                    <Chart
                      options={chartData.options}
                      series={chartData.series}
                      type="donut"
                      height={350}
                    />
                  )}
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Explanation</h3>
                <p className="text-gray-700">
                  Your initial deposit of ₹{formData.initialDeposit} will grow to ₹{results.finalAmount} over {results.totalYears} years,
                  earning ₹{results.interestEarned} in interest due to {results.periodsPerYear} compounding periods per year.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            How to Use APY Calculator <FaInfoCircle className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <ol className="list-decimal pl-8 space-y-4">
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Enter your initial deposit amount</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Input the annual interest rate</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Select the compounding frequency</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Enter the number of years</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Click "Calculate APY" to see your results</span>
              </li>
            </ol>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Features and Benefits <FaChartLine className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>APY calculation with different compounding frequencies</li>
                  <li>Future value projection</li>
                  <li>Interest earned calculation</li>
                  <li>Easy-to-understand results</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Benefits</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Compare different investment options</li>
                  <li>Understand the power of compounding</li>
                  <li>Make informed financial decisions</li>
                  <li>Plan for long-term growth</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Frequently Asked Questions <FaQuestionCircle className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is APY?</h3>
                <p className="text-gray-700">
                  APY (Annual Percentage Yield) is the effective annual rate of return taking into account the effect of compounding interest.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How does compounding frequency affect APY?</h3>
                <p className="text-gray-700">
                  The more frequently interest is compounded (daily, monthly, etc.), the higher the APY will be, as you earn interest on previously earned interest.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Why is APY important?</h3>
                <p className="text-gray-700">
                  APY helps you compare different investment or savings options by showing the true annual return, taking into account the frequency of compounding.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How accurate is this calculator?</h3>
                <p className="text-gray-700">
                  This calculator provides an estimate based on the inputs you provide. Actual results may vary based on specific financial institution policies and other factors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APYCalculator;
